import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  idealLevel, tilesForViewport, tileRectInBase, tileKey,
} from "@/lib/tilePyramid";
import {
  tileKey as canonicalTileKey, auditVisibleTiles,
} from "@/lib/tileIdentity";

/**
 * TileLayer — viewport-priority tile renderer.
 *
 * Renders INSIDE the canvas's already-transformed wrapper, in the
 * natural-image-pixel coordinate space (i.e. the L0 width × L0 height
 * rectangle). The transform applied by the parent SheetCanvas scales +
 * translates this whole subtree, so tile positions express in L0 pixels
 * stay spatially locked regardless of viewport.
 *
 * Operational rules:
 *   - The low-res base raster underneath is ALWAYS visible (zero load
 *     gaps, zero "tile popping"). TileLayer just paints HIGHER-resolution
 *     tiles on top for the visible viewport — they fade in at 200ms.
 *   - LRU memory cap: keep at most LRU_CAP recently-touched tiles in
 *     memory. Cold tiles are unloaded by removing them from state.
 *   - Visible-viewport-only loading. No eager pyramid downloads.
 *   - When the viewport changes, recompute the visible tile set in a
 *     debounced rAF — never per-pixel during a pan gesture.
 *
 * Props:
 *   sheetId          — id of the current sheet
 *   viewport         — { tx, ty, s } from the canvas physics
 *   containerDims    — { w, h }
 *   onMetaLoaded(meta) — optional listener so the canvas can adopt L0 dims
 */
const LRU_CAP = 96;
const VIEWPORT_DEBOUNCE_MS = 30;

export default function TileLayer({ sheetId, viewport, containerDims, onMetaLoaded, revisionId }) {
  const [meta, setMeta] = useState(null);
  // Loaded tile state: Map<key, { tile, url, status, generation, sheetId, revisionId }>.
  const [, forceRender] = useState(0);
  const tilesRef = useRef(new Map());
  const lruRef = useRef([]);   // [key, key, ...] — newest first
  // Iter 168 — Field-Trust Recovery.
  // Track the currently-active pyramid level so we can render ONLY the
  // active level's tiles. Previously the JSX iterated every entry in
  // tilesRef, which meant tiles from the previous zoom level stayed
  // painted at opacity 1 on top of the new-level tiles causing the
  // stacking + duplicate-imagery bleed the operator reported.
  const [activeLevel, setActiveLevel] = useState(null);
  // Increment on every sheetId change so stale in-flight <img> onLoad
  // callbacks can be identified and ignored (defect T-4).
  const generationRef = useRef(0);
  // IP-001 Step 2 — AbortController for the tile_meta fetch. On sheet
  // change we abort the outgoing metadata request so a late response
  // cannot mutate active state.
  const metaAbortRef = useRef(null);
  // IP-001 Step 1 — runtime tile hygiene stats surfaced on the DOM as
  // `data-*` attributes so cross-browser probes can assert them.
  const [hygiene, setHygiene] = useState({
    duplicates: 0, staleGen: 0, crossSheet: 0, crossRevision: 0, evicted: 0,
  });

  // Load meta on mount / sheet change.
  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    // IP-001 Step 3 — cache ownership: full cache purge on sheet change.
    // Every tile in the cache belongs to the outgoing generation and
    // cannot legally paint against the incoming sheet. Iter168 already
    // did this; retained for explicit generation-scoped cleanup.
    tilesRef.current.clear();
    lruRef.current = [];
    setActiveLevel(null);
    // FP-058 (2026-09-09 audit remediation) — the tile CACHE was purged
    // above, but `meta` (the previous sheet's pyramid dimensions/level
    // count) was never cleared, only overwritten once the new sheet's
    // fetch resolved. In the window between a sheet switch and that
    // fetch resolving — and PERMANENTLY if the new sheet has no
    // pyramid at all and the fetch 404s — the debounced tile-
    // computation effect below could still run against the OUTGOING
    // sheet's stale `meta`, repopulating the (correctly re-tagged, but
    // geometrically wrong) tile cache from the previous sheet's pyramid
    // coordinates. Clearing `meta` here means the render guard
    // (`if (!meta) return null`) suppresses ANY tile paint until the
    // new sheet's own metadata is confirmed — no stale-sheet geometry
    // can ever be used to compute "visible tiles" for the new sheet.
    setMeta(null);
    generationRef.current += 1;
    forceRender((n) => n + 1);
    // IP-001 Step 2 — abort any in-flight metadata request from the
    // outgoing sheet so its callback cannot land on the new sheet.
    if (metaAbortRef.current) {
      try { metaAbortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const ctrl = (typeof AbortController !== "undefined")
      ? new AbortController() : null;
    metaAbortRef.current = ctrl;
    api.get(`/sheets/${sheetId}/tile_meta`, ctrl ? { signal: ctrl.signal } : {})
      .then((r) => {
        if (cancelled) return;
        setMeta(r.data);
        onMetaLoaded?.(r.data);
      }).catch((e) => {
        if (cancelled) return;
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        // No pyramid available — base raster handles everything. Silent.
        console.debug("[TileLayer] tile_meta unavailable:", e?.response?.status);
      });
    return () => {
      cancelled = true;
      if (ctrl) { try { ctrl.abort(); } catch (_) { /* noop */ } }
      if (metaAbortRef.current === ctrl) metaAbortRef.current = null;
    };
    // FP-059 — a revision change must invalidate exactly like a sheet
    // change (defense-in-depth: today this architecture creates a new
    // sheet id per revision so `sheetId` already changes too, but the
    // revision identity check must be meaningful on its own, not inert).
  }, [sheetId, revisionId, onMetaLoaded]);

  // Debounced visible-tile computation. Runs after viewport settles to
  // avoid spamming requests during a fast pan/zoom gesture.
  const debounceRef = useRef(0);
  useEffect(() => {
    if (!meta || !viewport || !containerDims?.w) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const z = idealLevel(viewport, containerDims, meta);
      const visible = tilesForViewport(viewport, containerDims, meta, z);
      // Iter 168 — Field-Trust Recovery: record the active level so the
      // render pass can gate stale-level tiles.
      setActiveLevel(z);
      // Always also keep tiles already loaded that are still visible — no
      // hard unload during the same gesture.
      const tiles = tilesRef.current;
      const lru = lruRef.current;
      const currentGen = generationRef.current;
      const currentRev = revisionId || "r0";
      for (const t of visible) {
        const k = tileKey(t);
        if (!tiles.has(k)) {
          tiles.set(k, {
            tile: t,
            url: buildTileUrl(sheetId, t),
            status: "loading",
            // IP-001 Step 1 — stamp origin identity so runtime audit
            // can detect stale-generation / cross-sheet / cross-revision.
            generation: currentGen,
            sheetId,
            revisionId: currentRev,
          });
        }
        // Touch in LRU.
        const idx = lru.indexOf(k);
        if (idx >= 0) lru.splice(idx, 1);
        lru.unshift(k);
      }
      // Iter 168 — Field-Trust Recovery: aggressively evict tiles that
      // are not at the active level. This is what prevents duplicate
      // imagery and stale-level bleed on zoom transitions. The base
      // raster remains underneath as the transition fallback.
      const toEvict = [];
      for (const [k, entry] of tiles.entries()) {
        if (entry.tile.z !== z) toEvict.push(k);
        // IP-001 Step 1 — runtime tile hygiene: also evict any entry
        // whose origin identity no longer matches the current sheet /
        // revision / generation. This guarantees no previous-sheet
        // imagery can survive a sheet switch even if the level filter
        // above would have kept it.
        else if (entry.sheetId !== sheetId ||
                 entry.revisionId !== currentRev ||
                 entry.generation !== currentGen) {
          toEvict.push(k);
        }
      }
      for (const k of toEvict) {
        tiles.delete(k);
        const idx = lru.indexOf(k);
        if (idx >= 0) lru.splice(idx, 1);
      }
      // Standard LRU eviction bound.
      while (lru.length > LRU_CAP) {
        const k = lru.pop();
        tiles.delete(k);
      }
      // IP-001 Step 1 — runtime tile-hygiene AUDIT. Build canonical
      // (tenant/sheet/revision/generation/level/x/y) keys for every
      // currently-mounted tile and count any invariant violation. Any
      // detected violation is evicted from the cache in the same pass
      // so the render below cannot show it. Counts are surfaced on the
      // DOM for cross-browser probes to assert = 0.
      const canonicalKeys = [];
      const keyToLocal = new Map();
      for (const [k, e] of tiles.entries()) {
        const canon = canonicalTileKey({
          sheetId: e.sheetId,
          revisionId: e.revisionId,
          generation: e.generation,
          level: e.tile.z, x: e.tile.x, y: e.tile.y,
        });
        canonicalKeys.push(canon);
        keyToLocal.set(canon, k);
      }
      const audit = auditVisibleTiles(canonicalKeys, {
        currentSheetId: sheetId,
        currentRevisionId: currentRev,
        currentGeneration: currentGen,
      });
      let evictedByAudit = 0;
      if (audit.duplicates || audit.staleGen ||
          audit.crossSheet || audit.crossRevision) {
        // Walk every canonical key that fails the invariants and drop
        // the matching cache entry. This is defense-in-depth on top of
        // the identity-guarded eviction above — if either code path
        // misses a violation, the audit sweeps it away.
        const seen = new Set();
        for (const canon of canonicalKeys) {
          const p = /^([^:]+)::([^:]+)::([^:]+)::g(\d+)::L(\d+)::(\d+)_(\d+)$/.exec(canon);
          if (!p) continue;
          const sh = p[2], rev = p[3], gen = parseInt(p[4], 10);
          const violates = sh !== sheetId
            || rev !== currentRev
            || gen < currentGen;
          const dup = seen.has(canon);
          seen.add(canon);
          if (violates || dup) {
            const k = keyToLocal.get(canon);
            if (k && tiles.has(k)) {
              tiles.delete(k);
              const idx = lru.indexOf(k);
              if (idx >= 0) lru.splice(idx, 1);
              evictedByAudit += 1;
            }
          }
        }
      }
      setHygiene({
        duplicates: audit.duplicates,
        staleGen: audit.staleGen,
        crossSheet: audit.crossSheet,
        crossRevision: audit.crossRevision,
        evicted: evictedByAudit,
      });
      forceRender((n) => n + 1);
    }, VIEWPORT_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [meta, viewport, containerDims, sheetId, revisionId]);

  if (!meta) return null;
  const base = meta.levels[0];
  // We sit inside the canvas's transformed wrapper. Position absolutely so
  // we layer on top of the base raster but inherit the parent transform.
  // Tiles are scaled to fill 100% of the wrapper (which is the base
  // raster's natural pixel dims, NOT necessarily equal to L0 dims). The
  // CSS-pixel positions are expressed as PERCENT so the layer auto-aligns
  // when the canvas swaps between dpr=1 and dpr=2 base rasters.
  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      data-testid="tile-layer"
      data-tile-base-w={base.width}
      data-tile-base-h={base.height}
      data-active-level={activeLevel ?? ""}
      data-mounted-tile-count={tilesRef.current.size}
      data-hygiene-duplicates={hygiene.duplicates}
      data-hygiene-stale-gen={hygiene.staleGen}
      data-hygiene-cross-sheet={hygiene.crossSheet}
      data-hygiene-cross-revision={hygiene.crossRevision}
      data-hygiene-evicted={hygiene.evicted}
    >
      {Array.from(tilesRef.current.values())
        // Iter 168 — Field-Trust Recovery: gate on active level so
        // stale-level tiles never paint (prevents stacking + bleed).
        .filter(({ tile }) => activeLevel == null || tile.z === activeLevel)
        .map(({ tile, url, status }) => {
          // Capture the generation at render-time. If the sheet changes
          // between when this <img> mounts and when its `onLoad` fires,
          // generationRef.current will have moved on and we'll ignore
          // the stale commit. This closes tile-defect T-4.
          const gen = generationRef.current;
        const r = tileRectInBase(tile, meta);
        // Express position as percentage of the L0 image — the parent
        // wrapper sizes itself to the base raster which is also a render
        // of the same PDF, so a percentage anchor remains true regardless
        // of which dpr-base is currently mounted.
        const leftPct   = (r.left / base.width) * 100;
        const topPct    = (r.top / base.height) * 100;
        const widthPct  = (r.width / base.width) * 100;
        const heightPct = (r.height / base.height) * 100;
        return (
          <img
            key={tileKey(tile)}
            src={url}
            alt=""
            draggable={false}
            data-tile-key={tileKey(tile)}
            onLoad={() => {
              if (gen !== generationRef.current) return;   // stale sheet — ignore
              const k = tileKey(tile);
              const t = tilesRef.current.get(k);
              if (t) {
                t.status = "ready";
                forceRender((n) => n + 1);
              }
            }}
            onError={() => {
              if (gen !== generationRef.current) return;   // stale sheet — ignore
              const k = tileKey(tile);
              const t = tilesRef.current.get(k);
              if (t) {
                t.status = "error";
                forceRender((n) => n + 1);
              }
            }}
            style={{
              position: "absolute",
              left: `${leftPct}%`,
              top: `${topPct}%`,
              width: `${widthPct}%`,
              height: `${heightPct}%`,
              opacity: status === "ready" ? 1 : 0,
              transition: "opacity 200ms ease-out",
              pointerEvents: "none",
              // Force GPU compositor → smooth fade-in.
              transform: "translateZ(0)",
              imageRendering: "auto",
            }}
          />
        );
      })}
    </div>
  );
}


function buildTileUrl(sheetId, t) {
  const token = (typeof localStorage !== "undefined" && localStorage.getItem("fop_bearer")) || "";
  return `${process.env.REACT_APP_BACKEND_URL}/api/sheets/${sheetId}/tile_pyramid/${t.z}/${t.x}_${t.y}.png?token=${token}`;
}
